import React, { Fragment } from "react";
import { ChildNavigationItem, DesktopDashboardProps, NavigationItem } from "../Dashboard.types";
import { ChevronDownIcon, ChevronLeftIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { BrandIcons, Link, Text, Translate } from "../../UI";
import { RouteConstants } from "../../../Routes";
import { useLocation } from "react-router-dom";
import { IPermission, useLogin, useCapabilities } from "../../../services";
import { useInteriorDesignerEnabled } from "../../../hooks/useInteriorDesignerEnabled";
import { useSalesAccess } from "../../../services/crm";
import { useSidebarCollapsed } from "../../../hooks";
import { CollapsedNavGroup } from "./CollapsedNavGroup.component";
import { isHighAccess } from "../../../utils";

function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * Kept in sync by hand with the `lg:pl-*` offset on `<main>` in
 * Dashboard.component.tsx — Tailwind cannot share a value between them.
 */
export const SIDEBAR_WIDTH_CLASS = 'lg:w-72';
export const SIDEBAR_COLLAPSED_WIDTH_CLASS = 'lg:w-[4.5rem]';

export const Navigator: React.FunctionComponent<DesktopDashboardProps> = (props: DesktopDashboardProps): JSX.Element => {
    const { collapsed } = useSidebarCollapsed();

    return (
        <>
            {/* Static sidebar for desktop — the only instance that can collapse. */}
            <div className={classNames(
                'hidden z-10 lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-[width] duration-200 ease-out print:!hidden',
                collapsed ? SIDEBAR_COLLAPSED_WIDTH_CLASS : SIDEBAR_WIDTH_CLASS
            )}>
                <Navigation key="desktop-sidebar" {...props} collapsible={true} />
            </div>

            <div className="
                lg:hidden z-10 fixed inset-y-0 flex w-72 flex-col
            ">
                <Navigation key="mobile-sidebar" {...props} />
            </div>
        </>
    )
}

/**
 * Brand accent, sampled from the Wallmates logo in each theme. Set explicitly on
 * the icon so it wins over the label colour that active/hover states apply to
 * the row; the brand icons are CSS masks over `bg-current`, so a text colour is
 * exactly what tints them.
 */
const NavIconStyles = "h-6 w-6 shrink-0 text-brand dark:text-brand-dark";

const ContactUsIcon = BrandIcons['contact-us'];

const NavItemStyles = "flex items-center gap-x-4 px-6 py-2.5 text-[13px] font-semibold leading-5 dark:text-white text-black hover:bg-surface-nav-active cursor-pointer";

const Navigation: React.FunctionComponent<DesktopDashboardProps> = (props: DesktopDashboardProps): JSX.Element => {
    const { navigation, setNavigation, closeSidebar, collapsible = false } = props;

    const { collapsed: collapsedPreference, toggle: toggleCollapsed } = useSidebarCollapsed();

    // The mobile drawer renders the same component but always in full form.
    const collapsed = collapsible && collapsedPreference;

    const { pathname } = useLocation();
    const [user] = useLogin();
    const { enabled: interiorDesignerEnabled } = useInteriorDesignerEnabled();
    const [salesAccess, salesAccessFunctions] = useSalesAccess();

    // Resolve the sales grant once per session. useSalesAccess caches into a
    // jotai atom and no-ops when already resolved, so this fires a single request
    // for the whole dashboard rather than one per consumer.
    const salesAccessRef = React.useRef(salesAccessFunctions);
    salesAccessRef.current = salesAccessFunctions;
    React.useEffect(() => {
        salesAccessRef.current.resolve();
    }, []);

    // Named flags resolved for nav gating. Add an entry here when a nav item
    // gains a featureFlag; anything absent is treated as unknown and stays shown.
    const featureFlagState: Record<string, boolean> = {
        'enable-interior-designer': interiorDesignerEnabled
    };
    const { hasShopify, resolved: capabilitiesResolved, load: loadCapabilities } = useCapabilities();

    React.useEffect(() => { loadCapabilities(); }, [loadCapabilities]);

    // Staff accounts are not tied to a storefront, so they keep every screen.
    const shopifyGateExempt = isHighAccess(user);

    // #region isShopifyGated
    /**
     * Hidden while capabilities are still resolving as well as when the brand has
     * no store — showing then hiding a nav item on hydration reads as a glitch.
     */
    const isShopifyGated = (item: { requiresShopify?: boolean }): boolean => {
        if (!item.requiresShopify || shopifyGateExempt) return false;
        return !capabilitiesResolved || !hasShopify;
    };

    const userPermission = (user?.permissions?.permission_id as IPermission || undefined)?.permission;

    // #region hasPermission
    /**
     * Permission tiers are NOT hierarchical here — 'USER' does not include
     * TECHNICIAN, and 'ADMIN' does not include SUPERUSER — so each level lists
     * the roles that satisfy it explicitly.
     */
    const hasPermission = (level: NavigationItem['permissionLevel'] | undefined): boolean => {
        if (!userPermission) return false;

        switch (level) {
            case 'USER':
                return ['USER', 'ADMIN', 'SUPERUSER', 'SUPERADMIN'].includes(userPermission);
            case 'ADMIN':
                return ['ADMIN', 'SUPERADMIN'].includes(userPermission);
            case 'TECHNICIAN':
                return ['TECHNICIAN', 'SUPERUSER', 'SUPERADMIN'].includes(userPermission);
            case 'SUPERUSER':
                return ['SUPERUSER', 'SUPERADMIN'].includes(userPermission);
            case 'SUPERADMIN':
                return userPermission === 'SUPERADMIN';
            case 'EVERYBODY':
                return ['USER', 'ADMIN', 'TECHNICIAN', 'SUPERUSER', 'SUPERADMIN'].includes(userPermission);
            // A child with no explicit level inherits its group's visibility.
            case undefined:
                return true;
            default:
                return false;
        }
    };

    // #region isChildVisible
    /**
     * Children used to bypass role filtering entirely, so an inventory child
     * gated to ADMIN was shown to every TECHNICIAN who could see the group.
     */
    const isChildVisible = (child: ChildNavigationItem): boolean =>
        !isShopifyGated(child)
        && hasPermission(child.permissionLevel)
        // Manager-only sales surfaces: hide rather than render a row that only
        // resolves to a "No Access" page.
        && (!child.requiresSalesManager || salesAccess.isManager);

    // #region matchesPath
    /**
     * Prefix matching so `/orders/123` still highlights Orders. Exact-only
     * matching left a detail page with nothing highlighted; naive prefix matching
     * would light up BOTH Orders and Place Order on `/orders/new`, so the most
     * specific href wins and the others are compared for exact equality.
     */
    const bestMatchHref = React.useMemo<string | null>(() => {
        const candidates: string[] = [];
        navigation.forEach((nav: NavigationItem) => {
            candidates.push(nav.href);
            nav.children?.forEach((child: ChildNavigationItem) => candidates.push(child.href));
        });

        return candidates
            .filter((href: string) => href.startsWith('/') && (pathname === href || pathname.startsWith(`${href}/`)))
            .sort((a: string, b: string) => b.length - a.length)[0] ?? null;
    }, [navigation, pathname]);

    const imagesBucket = process.env.REACT_APP_AWS_BUCKET_IMAGES || '';

    React.useEffect(() => {
        // Keep the highlighted option correct on refresh and on any direct
        // navigation (a redirect, a link from inside a page, the back button).
        setNavigation(navigation.map((nav: NavigationItem) => {
            const selfMatch = nav.href === bestMatchHref;
            let foundChildMatch = false;

            if (nav.children) {
                nav.children = nav.children.map((child: ChildNavigationItem) => {
                    child.current = child.href === bestMatchHref;
                    if (child.current) foundChildMatch = true;
                    return child;
                });
            }

            nav.current = selfMatch || foundChildMatch;
            nav.expanded = foundChildMatch;

            return nav;
        }))
    }, [bestMatchHref])

    const getIsNavExpanded = (item: NavigationItem) => {
        return item.current ||
            (item.children && item.children.filter((child: ChildNavigationItem) => child.current).length > 0) ||
            item.expanded;
    }

    const updateCurrentItem = (item: NavigationItem, isCurrent = true) => {
        const nav = navigation.map((i: NavigationItem) => {
            if (i.href === item.href) {
                i.current = isCurrent;
                if (i.children) {
                    i.expanded = true;
                }
            } else if (isCurrent) {
                i.expanded = false;
                i.current = false;
                if (i.children) {
                    i.children = i.children.map((child: ChildNavigationItem) => {
                        child.current = false;
                        return child;
                    })
                }
            }
            return i;
        });
        setNavigation(nav);
        closeSidebar(false);
    }

    const updateCurrentItemChild = (item: NavigationItem, child: ChildNavigationItem) => {
        const nav = navigation.map((i: NavigationItem) => {
            if (i.href === item.href) {
                i.current = true;
                if (i.children) {
                    i.expanded = true;
                    i.children = i.children.map((c: ChildNavigationItem) => {
                        if (c.href === child.href) {
                            c.current = true;
                        } else {
                            c.current = false;
                        }
                        return c;
                    })
                }
            } else {
                i.current = false;
                if (i.children) {
                    i.expanded = false;
                    i.children = i.children.map((c: ChildNavigationItem) => {
                        c.current = false;
                        return c;
                    });
                }
            }
            return i;
        });
        setNavigation(nav);
        closeSidebar(false);
    }

    // #region collapsedNavItem
    /**
     * Icons-only row. A 72px rail has no room for an indented child tree, so a
     * group opens its children in a flyout instead — hiding them would strand
     * every destination that only exists as a sub-item.
     */
    const collapsedNavItem = (item: NavigationItem) => {
        const label = item.fallbackLabel ?? item.name;

        const rowClassName = classNames(
            item.current ?
                'bg-surface-nav-active dark:text-white text-black' :
                'dark:text-gray-300 dark:group-hover:text-white group-hover:bg-surface-nav-active text-gray-600 group-hover:text-black',
            'flex items-center justify-center w-full py-2 rounded-md cursor-pointer'
        );

        const visibleChildren = item.children?.filter(isChildVisible) ?? [];

        if (visibleChildren.length > 0) {
            return (
                <CollapsedNavGroup
                    key={item.name}
                    item={item}
                    items={visibleChildren}
                    iconClassName={NavIconStyles}
                    rowClassName={rowClassName}
                    onSelectSelf={() => updateCurrentItem(item)}
                    onSelectChild={(child: ChildNavigationItem) => updateCurrentItemChild(item, child)}
                />
            );
        }

        return (
            <li key={item.name} className="group flex w-full">
                <Link
                    href={item.href}
                    action={() => { updateCurrentItem(item) }}
                    title={label}
                    aria-label={label}
                    className={rowClassName}
                >
                    <span className="relative">
                        <item.icon className={NavIconStyles} aria-hidden="true" />
                        {item.featured && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-brand ring-2 ring-surface-nav" aria-hidden="true" />}
                    </span>
                </Link>
            </li>
        )
    }

    const navItem = (item: NavigationItem) => {
        if (collapsed) return collapsedNavItem(item);

        return (
            <Fragment key={item.name}>
                <li className="group flex flex-row flex-grow w-full">
                    <Link
                        href={item.href}
                        action={() => {
                            updateCurrentItem(item)
                        }}
                        className={classNames(
                            item.current ?
                                'bg-surface-nav-active dark:text-white text-black' :
                                'dark:text-gray-300 dark:group-hover:text-white group-hover:bg-surface-nav-active text-gray-600 group-hover:text-black',
                            'flex gap-x-3 px-2 py-1.5 text-[13px] leading-5 font-semibold flex-grow',
                            item.children && item.children.length > 0 ?
                                'rounded-l-md' :
                                'rounded-md',
                            'cursor-pointer'
                        )}
                    >
                        <item.icon className={NavIconStyles} aria-hidden="true" />
                        <Text className="!text-[13px] !leading-5"><Translate translation_key={item.name} fallback={item.fallbackLabel}></Translate></Text>
                        {item.featured && <span className="ml-auto self-center rounded-full bg-brand px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-white">New</span>}
                    </Link>
                    {
                        item.children && item.children.length > 0 ?
                            <div
                                onClick={() => {
                                    updateCurrentItem(item)
                                }}
                                className={classNames(
                                    item.current
                                        ? 'bg-surface-nav-active dark:text-white text-black'
                                        : 'dark:text-gray-300 dark:group-hover:text-white group-hover:bg-surface-nav-active text-gray-600 group-hover:text-black',
                                    'rounded-r-md px-2 py-1.5 text-[13px] leading-5 font-semibold w-10'
                                )}
                            >
                                {
                                    getIsNavExpanded(item) ?
                                        <ChevronDownIcon className="h-6 w-6 shrink-0 cursor-pointer text-brand dark:text-brand-dark" /> :
                                        <ChevronLeftIcon className="h-6 w-6 shrink-0 cursor-pointer text-brand dark:text-brand-dark" />
                                }
                            </div> :
                            null
                    }
                </li>
                {
                    item.children && item.children.length > 0 && item.expanded ?
                        item.children.filter(isChildVisible).map((child: ChildNavigationItem) => (
                            <li className="pl-6 ml-5 border-l dark:border-gray-400 border-gray-600" key={child.name}>
                                <Link
                                    href={child.href}
                                    action={() => {
                                        updateCurrentItemChild(item, child)
                                    }}
                                    className={classNames(
                                        child.current ?
                                            'bg-surface-nav-active dark:text-white text-black' :
                                            'dark:text-gray-300 dark:group-hover:text-white group-hover:bg-surface-nav-active text-gray-600 group-hover:text-black',
                                        'flex gap-x-3 px-2 py-1.5 rounded-md text-[13px] leading-5 font-semibold flex-grow',
                                        'cursor-pointer'
                                    )}
                                >
                                    <child.icon className={NavIconStyles} aria-hidden="true" />
                                    <Text className="!text-[13px] !leading-5"><Translate translation_key={child.name} fallback={child.fallbackLabel}></Translate></Text>
                                </Link>
                            </li>
                        )) : null
                }
            </Fragment>
        )
    }

    return (
        <>
            <div className={classNames(
                'flex grow flex-col gap-y-2 bg-surface-nav nav-scroll',
                collapsed ? 'px-2' : 'px-6'
            )}>
                <div key={"company-logo"} className={classNames(
                    'flex shrink-0 items-center',
                    collapsed ? 'h-20 justify-center pt-4' : 'h-32 pt-6'
                )}>
                    <img
                        className={classNames('block dark:hidden', collapsed ? 'w-[40px] h-[40px]' : 'w-[60px] h-[60px]')}
                        src={`https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Icon-light-mode.png`}
                    />
                    <img
                        className={classNames('hidden dark:block', collapsed ? 'w-[40px] h-[40px]' : 'w-[60px] h-[60px]')}
                        src={`https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Icon-dark-mode.png`}
                    />
                </div>

                {/* Collapse toggle — desktop rail only; the mobile drawer closes instead. */}
                {collapsible ? (
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                        aria-expanded={!collapsed}
                        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                        className={classNames(
                            'flex items-center gap-x-2 py-1.5 rounded-md text-[13px] font-semibold shrink-0',
                            'dark:text-gray-400 text-gray-600 hover:bg-surface-nav-active dark:hover:text-white hover:text-black',
                            collapsed ? 'justify-center' : 'justify-end px-2'
                        )}
                    >
                        {collapsed
                            ? <ChevronDoubleRightIcon className="h-5 w-5 shrink-0 text-brand dark:text-brand-dark" aria-hidden="true" />
                            : <ChevronDoubleLeftIcon className="h-5 w-5 shrink-0 text-brand dark:text-brand-dark" aria-hidden="true" />}
                    </button>
                ) : null}

                <nav key={"navigation-main"} className="flex flex-1 flex-col">
                    <ul className="flex justify-between flex-col mb-4 h-full">
                        <li key={"main-nav"}>
                            <ul key="main-nav-ul" className={classNames('space-y-0.5', collapsed ? '' : '-mx-2')}>
                                {navigation.map((item: NavigationItem) => {
                                    if (!userPermission) {
                                        return null;
                                    }

                                    // Feature-flag filter: hide the item when its gating flag is
                                    // known and off. Unknown flags stay visible, preserving the
                                    // behaviour from when this was hard-coded to one flag name.
                                    if (item.featureFlag && featureFlagState[item.featureFlag] === false) {
                                        return null;
                                    }

                                    // Sales access is a grant, NOT a permission tier — do not add a
                                    // SALES arm to hasPermission. While the grant is still
                                    // resolving the item stays hidden so the tab cannot flash in
                                    // for users who will turn out not to have it.
                                    if (item.requiresSalesAccess && !salesAccess.isRep) {
                                        return null;
                                    }

                                    // Capability filter: Shopify-dependent screens are hidden for
                                    // brands with no storefront (the artist-mode default).
                                    if (isShopifyGated(item)) {
                                        return null;
                                    }

                                    if (!hasPermission(item.permissionLevel)) {
                                        return null;
                                    }

                                    // A group whose every child is filtered out is an empty
                                    // expander — drop it rather than render a dead row.
                                    if (item.children && item.children.length > 0 && !item.children.some(isChildVisible)) {
                                        return null;
                                    }

                                    return navItem(item);
                                })}
                            </ul>
                        </li>
                        {/*
                          Contact Us keeps its position at the foot of the sidebar and
                          simply moves down into the slot the email-address button used
                          to occupy — that button now lives in the header's account menu.
                        */}
                        <li key="sidebar-footer" className="flex flex-col">
                            <Link
                                href={RouteConstants.CONTACT_US}
                                title="Contact Us"
                                className={collapsed
                                    ? 'flex items-center justify-center w-full py-2 rounded-md cursor-pointer dark:text-gray-300 text-gray-600 hover:bg-surface-nav-active dark:hover:text-white hover:text-black'
                                    : NavItemStyles + ' pl-3'}
                            >
                                <ContactUsIcon key={"contact-us-icon"} className={NavIconStyles} />
                                {collapsed ? null : (
                                    <Text className="!text-[13px] !leading-5"><Translate translation_key="NAVIGATION.CONTACT_US" fallback="Contact Us" /></Text>
                                )}
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>
        </>
    )
}